"use client";

import React, { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, X } from "lucide-react";
import type { Node, Edge } from "@xyflow/react";
import type { IdeaNodeData, DbSchema } from "./types";
import { ContextEngineer } from "./context-engineer";

interface PromptModalProps {
  nodes: Node<IdeaNodeData>[];
  edges: Edge[];
  dbSchema: DbSchema;
  /** If set, the modal opens directly on this node's prompt tab. */
  initialNodeId?: string | null;
  onClose: () => void;
}

export function PromptModal({
  nodes,
  edges,
  dbSchema,
  initialNodeId,
  onClose,
}: PromptModalProps) {
  const engineer = useMemo(
    () => new ContextEngineer(nodes, edges, dbSchema),
    [nodes, edges, dbSchema],
  );

  const nodeIds = engineer.nodeIds;

  // "overview" = pipeline map, or a node id
  const [selected, setSelected] = useState<string>(initialNodeId ?? "overview");
  const [copied, setCopied] = useState(false);

  const prompt = useMemo(() => {
    if (selected === "overview") return engineer.getHighLevelOverview();
    try {
      return engineer.getPromptForNode(selected);
    } catch {
      return "";
    }
  }, [selected, engineer]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 12 }}
        transition={{ duration: 0.18 }}
        className="mx-4 flex h-[90%] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-950"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-white/5">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            KI-Prompt generieren
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-500 transition hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-green-500" /> Kopiert
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" /> Kopieren
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Sidebar: tab list */}
          <div className="flex w-48 shrink-0 flex-col gap-1 overflow-y-auto border-r border-gray-100 p-3 dark:border-white/5">
            <button
              onClick={() => setSelected("overview")}
              className={`rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                selected === "overview"
                  ? "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
                  : "text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5"
              }`}
            >
              Pipeline-Übersicht
            </button>

            {nodeIds.length > 0 && (
              <div className="mb-1 mt-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Nodes
              </div>
            )}

            {nodeIds.map((id) => (
              <button
                key={id}
                onClick={() => setSelected(id)}
                className={`truncate rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                  selected === id
                    ? "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"
                    : "text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5"
                }`}
                title={engineer.getNodeTitle(id)}
              >
                {engineer.getNodeTitle(id)}
              </button>
            ))}
          </div>

          {/* Prompt text area */}
          <pre className="flex-1 overflow-auto whitespace-pre-wrap p-5 font-mono text-xs leading-relaxed text-gray-700 dark:text-gray-300">
            {nodes.length === 0
              ? "Noch keine Nodes im Canvas. Erstelle zuerst Nodes und verbinde sie."
              : prompt}
          </pre>
        </div>
      </motion.div>
    </motion.div>
  );
}
